import {
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef
} from "react";
import lottie from "lottie-web";
import lottieApi from "lottie-api/dist/lottie_api";

const LottieAnimation = forwardRef((props, ref) => {
  ref = ref || createRef();
  const {
    path,
    loop,
    autoplay,
    rendererSettings,
    isPaused,
    isStopped,
    direction,
    width,
    height,
    ariaRole,
    ariaLabel,
    title,
    style
  } = props;
  const el = useRef(null);
  const anim = useRef(null);
  const animApi = useRef(null);

  const setDirection = useCallback(
    (direction = null) => {
      anim.current?.setDirection(direction);
    },
    [anim]
  );

  const play = useCallback(() => {
    anim.current?.play();
  }, []);

  const stop = useCallback(() => {
    anim.current?.stop();
  }, []);

  const pause = useCallback(() => {
    anim.current?.pause();
  }, []);

  useEffect(() => {
    if (isStopped) {
      stop();
    } else {
      play();
    }
  }, [isStopped, play, stop]);

  useEffect(() => {
    if (isPaused) pause();
  }, [isPaused, pause]);

  useEffect(() => {
    if (direction) setDirection(direction);
  }, [direction, setDirection]);

  useEffect(() => {
    const lottieOptions = {
      container: el.current || null,
      renderer: "svg",
      loop: !!loop,
      autoplay: !!autoplay,
      path,
      rendererSettings
    };
    anim.current = lottie.loadAnimation(lottieOptions);
    animApi.current = lottieApi.createAnimationApi(anim.current);

    return () => {
      anim.current?.destroy();
    };
  }, [autoplay, loop, path, rendererSettings]);

  useImperativeHandle(ref, () => ({
    play: play,
    setDirection: setDirection,
    anim: anim,
    animApi: animApi,
    pause: pause,
    stop: stop
  }));

  const getSize = initial => {
    let size;

    if (typeof initial === "number") {
      size = `${initial}px`;
    } else {
      size = initial || "100%";
    }

    return size;
  };

  const lottieStyles = {
    width: getSize(width),
    height: getSize(height),
    overflow: "hidden",
    margin: "0 auto",
    outline: "none",
    ...style
  };

  return (
    <div
      ref={el}
      style={lottieStyles}
      title={title}
      role={ariaRole}
      aria-label={ariaLabel}
      tabIndex="0"
    />
  );
});
LottieAnimation.displayName = "LottieAnimation";
export default LottieAnimation;
